import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './ftp-editor.css';

const TOKEN_UNSUBSCRIBE = '{{unsubscribe_link}}';
const TOKEN_WEBLINK = '{{weblink}}';

export interface FtpLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (url: string, linkText: string, hasSelection: boolean) => void;
  hasSelection?: boolean;
}

export function FtpLinkModal({
  isOpen,
  onClose,
  onInsert,
  hasSelection = false,
}: FtpLinkModalProps) {
  const [url, setUrl] = useState('https://');
  const [linkText, setLinkText] = useState('Click here');

  useEffect(() => {
    if (isOpen) {
      setUrl('https://');
      setLinkText('Click here');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInsert = () => {
    onInsert(url, linkText, hasSelection);
    onClose();
  };

  return createPortal(
    <div className="ftp-modal-overlay active">
      <div
        className="ftp-modal ftp-link-modal"
        role="dialog"
        aria-labelledby="ftp-link-modal-title"
      >
        <div className="ftp-modal-header">
          <h3 id="ftp-link-modal-title">Insert Link</h3>
          <button type="button" className="ftp-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="ftp-modal-body">
          <div className="ftp-link-field">
            <label htmlFor="ftp-link-url">URL</label>
            <input
              id="ftp-link-url"
              type="text"
              className="ftp-link-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoComplete="off"
            />
            <div className="ftp-link-quick-btns">
              <button
                type="button"
                className={`ftp-btn-variable${url === TOKEN_UNSUBSCRIBE ? ' active' : ''}`}
                onClick={() => {
                  setUrl(TOKEN_UNSUBSCRIBE);
                  setLinkText('Unsubscribe');
                }}
              >
                {TOKEN_UNSUBSCRIBE}
              </button>
              <button
                type="button"
                className={`ftp-btn-variable${url === TOKEN_WEBLINK ? ' active' : ''}`}
                onClick={() => {
                  setUrl(TOKEN_WEBLINK);
                  setLinkText('View in browser');
                }}
              >
                {TOKEN_WEBLINK}
              </button>
            </div>
          </div>

          <div className="ftp-link-quick-descriptions">
            <p className="ftp-link-desc">
              <strong>{TOKEN_UNSUBSCRIBE}</strong> is replaced at send time with the recipient’s
              one-click unsubscribe URL. Use this for compliance-friendly unsubscribe links in
              marketing email.
            </p>
            <p className="ftp-link-desc">
              <strong>{TOKEN_WEBLINK}</strong> is replaced with a web-based view of the message.
              Subscribers who have trouble rendering HTML email can open that URL in a browser
              instead.
            </p>
          </div>

          {hasSelection ? null : (
            <div className="ftp-link-field">
              <label htmlFor="ftp-link-text">Link text</label>
              <input
                id="ftp-link-text"
                type="text"
                className="ftp-link-input"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                autoComplete="off"
              />
            </div>
          )}
        </div>
        <div className="ftp-modal-footer">
          <div />
          <div className="ftp-modal-actions">
            <button type="button" className="ftp-btn ftp-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="ftp-btn ftp-btn-primary" onClick={handleInsert}>
              Insert Link
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
